import * as React from "react";
import classNames from "classnames";
import { isSameDay } from "date-fns";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import {
	DropTarget,
	DropTargetConnector,
	DropTargetMonitor,
	ConnectDropTarget
} from "react-dnd";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { Table } from "../../api/tables";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Toolbar from "@material-ui/core/Toolbar";
import { Reservation } from "../../api/reservations";
import { ConnectedReservationItem } from "./reservation-item";
import { notEmpty, coordinatesInAngleRange } from "../../util/general-utility";
import { DropTargetData } from "./day-overview";
import { ConnectedTiteledPaper } from "../layout/titeled-paper";

interface State {}

export interface ReservationSliderProps {}

interface ConnectedReservationSliderProps extends WithStyles<typeof styles> {
	tables: Table[];
	reservations: Reservation[];
	selectedDate: Date;
	canDrop?: boolean;
	isOver?: boolean;
	connectDropTarget?: ConnectDropTarget;
}

type Props = ReservationSliderProps &
	ConnectedReservationSliderProps &
	InjectedTranslateProps;

export class ReservationSlider extends React.Component<Props, State> {
	public state: State = {};

	private startX: number = null;
	private startY: number = null;
	private actionHandled = false;
	private disableDND = false;

	public componentDidMount() {
		window.addEventListener("touchend", this.handleWindowTouchEnd);
	}

	public componentWillUnmount() {
		window.removeEventListener("touchend", this.handleWindowTouchEnd);
	}

	private handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
		const { clientX, clientY } = event.touches[0];

		this.startX = clientX;
		this.startY = clientY;
	};

	private handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
		const { clientX, clientY } = event.touches[0];

		if (this.actionHandled) {
			if (this.disableDND) {
				event.stopPropagation();
			}
		} else {
			if (
				coordinatesInAngleRange(
					this.startX,
					this.startY,
					clientX,
					clientY,
					[{ start: 335 }, { end: 25 }, { start: 155, end: 205 }]
				)
			) {
				this.disableDND = true;
				this.actionHandled = true;

				event.stopPropagation();
			} else {
				this.actionHandled = true;
			}
		}
	};

	private handleWindowTouchEnd = () => {
		this.startX = null;
		this.startY = null;
		this.actionHandled = false;
		this.disableDND = false;
	};

	private renderSlideTrack(reservations: Reservation[]) {
		return reservations.map(r => (
			<ConnectedReservationItem key={r.id} {...r} />
		));
	}

	private renderEmptyInfo() {
		const { classes } = this.props;

		return (
			<Typography className={classes.note}>
				Alle Reservierungen eingeteilt!
			</Typography>
		);
	}

	public render() {
		const {
			classes,
			reservations,
			connectDropTarget,
			selectedDate
		} = this.props;

		const openReservations = reservations.filter(
			r => !notEmpty(r.tableId) && isSameDay(r.date, selectedDate)
		);

		return (
			connectDropTarget &&
			connectDropTarget(
				<div
					className={classes.root}
					onTouchStart={this.handleTouchStart}
					onTouchMove={this.handleTouchMove}
				>
					<div
						className={classNames(classes.slideTrack, {
							[classes.slideTrackCenterContent]:
								openReservations.length === 0
						})}
					>
						{openReservations.length > 0
							? this.renderSlideTrack(openReservations)
							: this.renderEmptyInfo()}
					</div>
				</div>
			)
		);
	}
}

@DropTarget(
	"ReservationItem",
	{
		drop(): DropTargetData {
			return { tableId: null };
		}
	},
	(connect: DropTargetConnector, monitor: DropTargetMonitor) => ({
		connectDropTarget: connect.dropTarget(),
		isOver: monitor.isOver(),
		canDrop: monitor.canDrop()
	})
)
class DecoratedReservationSlider extends ReservationSlider {}

const mapStateToProps = (state: StoreState) => ({
	tables: state.tables,
	reservations: state.reservations,
	selectedDate: state.date
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			background: "#EBEEF1",
			overflow: "scroll",
			webkitOverflowScrolling: "touch",
			flexGrow: 1,
			display: "flex",
			flexDirection: "column"
		},
		slideTrack: {
			padding: 20,
			display: "flex",
			flexGrow: 1,

			"& > *": {
				flexShrink: 0
			},
			"& > * + *": {
				marginLeft: 15
			}
		},
		slideTrackCenterContent: {
			justifyContent: "center",
			alignItems: "center"
		},
		paper: {
			padding: 20
		},
		title: {
			marginBottom: 10
		},
		note: {
			fontSize: "1.5rem",
			fontWeight: 500,
			color: "transparent",
			display: "block",
			backgroundColor: "#a7a9ab",
			textShadow: "1px 1px 1px rgba(255, 255, 255, 0.5)",
			"-webkit-background-clip": "text",
			backgroundClip: "text"
		}
	});

export const ConnectedReservationSlider = translate("translations")(
	withStyles(styles)(connect(mapStateToProps)(DecoratedReservationSlider))
);
